import { useCallback } from 'react';

import { useCoachMark } from '../components/CoachMark/useCoachMark';
import type { DashboardPage } from '../modules/dashboard/components/Layout';
import { useUserProfile } from '../modules/dashboard/hooks/useUserProfile';
import type { UserProfile } from '../types/userProfile';

export interface UseProductTourOptions {
  onNavigate: (page: DashboardPage) => void;
  /**
   * When set (including `null`), `start` / `replay` use this row for onboarding + tour flags.
   * Pass the same object as the page's `useUserProfile().profile` so Profile doesn't depend on a second fetch.
   */
  gatesProfile?: UserProfile | null;
}

export interface UseProductTourResult {
  start: () => void;
  replay: () => void;
  /**
   * Force-start the tour bypassing all profile gates.
   * Use this for the Profile page "Start Tour" button so it always works.
   */
  forceStart: () => void;
  /** Whether the CoachMark tour overlay is currently visible. */
  isActive: boolean;
  /** Exposed so shell chrome can wait on profile without a second subscription. */
  profile: ReturnType<typeof useUserProfile>['profile'];
  isProfileLoading: boolean;
}

/**
 * First-visit tour + replay. Persists `tour_completed` on completion or skip.
 * Steps are pre-registered via TourProvider in AppProviders — no step array needed.
 */
export function useProductTour({
  gatesProfile: gatesProfileOpt,
}: UseProductTourOptions): UseProductTourResult {
  const { start: coachStart, isActive } = useCoachMark();
  const { profile: hookProfile, isLoading: isProfileLoading, save } = useUserProfile();

  const gatesProfile = gatesProfileOpt !== undefined ? gatesProfileOpt : hookProfile;

  const beginTour = useCallback(() => {
    coachStart({
      onComplete: () => {
        void save({ tourCompleted: true }).catch(() => {
          /* non-blocking; user can replay from profile if needed */
        });
      },
    });
  }, [coachStart, save]);

  const forceStart = useCallback(() => {
    if (isActive) return;
    beginTour();
  }, [beginTour, isActive]);

  const start = useCallback(() => {
    if (isActive) return;
    if (!gatesProfile?.onboardingCompleted) return;
    if (gatesProfile.tourCompleted) return;
    beginTour();
  }, [beginTour, isActive, gatesProfile?.onboardingCompleted, gatesProfile?.tourCompleted]);

  const replay = useCallback(() => {
    if (!gatesProfile?.id) return;
    forceStart();
  }, [forceStart, gatesProfile?.id]);

  return {
    start,
    replay,
    forceStart,
    isActive,
    profile: hookProfile,
    isProfileLoading,
  };
}
